import type { FC } from "react";
import styled from "styled-components";
import { colours, device } from "@helpers";
import type { IHelpsiteArticle, IHelpsiteFields } from "@types-contentful";
import FooterModule from "@components/FooterModule";
import SearchBar from "@components/NavigationBar/SearchBar";
import HelpTopicList from "./HelpTopicList";
import HelpFaqLinks from "./HelpFaqLinks";
import HelpMobileSupportFooter from "./HelpMobileSupportFooter";
import HelpDesktopSupportFooter from "./HelpDesktopSupportFooter";
import Link from "next/link";

// Desktop Part A: Header section with peach background
const HeaderSection = styled.div`
	background-color: ${colours.PALE_PEACH};
	padding: 40px 20px;
	display: flex;
	flex-direction: column;
	gap: 20px;
	width: 100%;
	box-sizing: border-box;
	overflow-x: hidden;
	
	@media (min-width: ${device.laptop}) {
		padding: 40px 265px;
	}
`;

const HeaderContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
	padding-top: 20px;
	width: 100%;
`;

const SearchBarWrapper = styled.div`
	width: 100%;
`;

const MainHeader = styled.h1`
	font-family: var(--taviraj-light);
	font-weight: 400;
	font-size: 30px;
	line-height: 1.4em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;

	@media (min-width: ${device.laptop}) {
		font-size: 44px;
		line-height: 1.3999999653209338em;
	}
`;

const MainSubCopy = styled.div`
	font-family: var(--euclid-medium);
	font-size: 16px;
	line-height: 1.2102272510528564em;
	color: ${colours.MIDNIGHT_BLUE};

	@media (min-width: ${device.laptop}) {
		font-family: var(--euclid-medium);
		font-size: 18px;
		line-height: 1.2679999669392903em;
	}

	p {
		margin: 0;
	}
`;

// Desktop Part B: Main content section
const MainContentSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
	padding: 0px 10px 80px;
	width: 100%;
	box-sizing: border-box;
	overflow-x: hidden;

	@media (min-width: ${device.laptop}) {
		gap: 50px;
		padding: 0px 265px 80px;
	}
`;

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: 30px;
	padding: 20px 10px;
	width: 100%;
	box-sizing: border-box;

	@media (min-width: ${device.laptop}) {
		padding: 0;
		width: 100%;
	}
`;

const SectionHeader = styled.h2`
	font-family: var(--taviraj-light);
	font-weight: 400;
	font-size: 30px;
	line-height: 1.6em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
	width: 100%;

	@media (min-width: ${device.laptop}) {
		font-size: 44px;
		line-height: 1.0909090909090908em;
		text-align: left;
	}
`;

const Divider = styled.div`
	height: 1px;
	background-color: rgba(0, 0, 0, 0.5);
	margin: 20px 0;
	width: 100%;

	@media (min-width: ${device.laptop}) {
		margin: 20px 0;
	}
`;

interface HelpMainPageProps {
	pageData: IHelpsiteFields;
	footer?: any;
	/** From getStaticProps: all helpsite topics, for FAQ article URLs */
	faqArticleTopicSlugById?: Record<string, string>;
}

const HelpMainPage: FC<HelpMainPageProps> = ({
	pageData,
	footer,
	faqArticleTopicSlugById = {},
}) => {
	const {
		mainHeader,
		mainSubCopy,
		topicsHeader,
		topics,
		faqHeader,
		helpsiteFaqLinks,
	} = pageData;

	const faqArticleLinks: IHelpsiteArticle[] = (helpsiteFaqLinks ?? []).filter(
		(entry): entry is IHelpsiteArticle => {
			if (
				!entry?.sys?.id ||
				entry?.sys?.contentType?.sys?.id !== "helpsiteArticle"
			) {
				return false;
			}

			const fields = entry.fields as
				| { articleRelativeUrl?: string }
				| undefined;
			return !!fields?.articleRelativeUrl;
		},
	);

	return (
		<>
			{/* Desktop Part A / Mobile Part A: Header Section */}
			<HeaderSection>
				<HeaderContent>
					{mainHeader && <MainHeader>{mainHeader}</MainHeader>}

					{/* {mainSubCopy && <MainSubCopy>{mainSubCopy}</MainSubCopy>} */}
					{mainSubCopy && (
						<MainSubCopy>
							Search here or browse by topic including our{" "}
							<Link
								target="_blank"
								href="https://felixmobile.com.au/help/network-troubleshooting"
							>
								Troubleshooting
							</Link>{" "}
							or{" "}
							<Link
								target="_blank"
								href="https://felixmobile.com.au/help/legal-and-privacy/how-do-i-make-a-complaint"
							>
								Complaints
							</Link>{" "}
							pages.
						</MainSubCopy>
					)}

					<SearchBarWrapper>
						<SearchBar fullWidth />
					</SearchBarWrapper>
				</HeaderContent>
			</HeaderSection>

			{/* Desktop Part B / Mobile Part B: Main Content Section */}
			<MainContentSection>
				{topicsHeader && topics && topics.length > 0 && (
					<Section>
						<SectionHeader>{topicsHeader}</SectionHeader>
						<HelpTopicList topics={topics} />
					</Section>
				)}

				{topics && topics.length > 0 && <Divider />}

				{faqHeader && faqArticleLinks.length > 0 && (
					<Section>
						<SectionHeader>{faqHeader}</SectionHeader>
						<HelpFaqLinks
							faqLinks={faqArticleLinks}
							faqArticleTopicSlugById={faqArticleTopicSlugById}
							topics={topics}
						/>
					</Section>
				)}
			</MainContentSection>

			{/* Mobile Part C: Hardcoded support footer */}
			<HelpMobileSupportFooter />

			{/* Desktop: Hardcoded support footer */}
			<HelpDesktopSupportFooter />

			{footer && <FooterModule {...footer} />}
		</>
	);
};

export default HelpMainPage;
